/**
 * Delegation Gap Appendix Slide
 * "Even When There's Desire to Help, You Can't"
 * Structural barriers to delegation, gendered cognitive stickiness,
 * 3 reason cards, Eve Rodsky "helper problem"
 * Sources: Weeks & Ruppanner 2024/2025, Daminger 2019, Eve Rodsky,
 *          Wegner 1987, BPS 2025
 */

import React from 'react';
import { colors } from '@config/colors';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
  AppendixColumn,
  AppendixSectionLabel,
  AppendixStatBar,
  AppendixStatNumber,
  AppendixStatDescription,
  AppendixStatSource,
  AccentCalloutBox,
  AccentCalloutTitle,
  AccentCalloutBody,
  AccentBorderedCard,
  AccentBorderedCardTitle,
  AccentBorderedCardBody,
  DarkInsightBox,
  DarkInsightTitle,
  DarkInsightBody,
  DarkInsightEmphasis,
  AppendixSourceFooter,
  ItalicCalloutBody,
  QuoteAttribution,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Component
// ============================================

export const DelegationGapSlide: React.FC = () => (
  <AppendixSlideLayout>
    <SlideHeader
      title="Even When There&#x2019;s Desire to Help, You Can&#x2019;t"
      subtitle="The imbalance persists not because partners don&#x2019;t want to help — but because the system makes delegation structurally impossible"
    />

    <AppendixContentArea>
      <AppendixColumn>
        <AppendixSectionLabel>The Intention&ndash;Action Gap</AppendixSectionLabel>

        <AppendixStatBar>
          <AppendixStatNumber>75%</AppendixStatNumber>
          <AppendixStatDescription>
            of couples say they want an equal split of household responsibilities &mdash;
            yet the reality looks nothing like it.{' '}
            <AppendixStatSource>(Weeks &amp; Ruppanner, 2024)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>63%</AppendixStatNumber>
          <AppendixStatDescription>
            of women in dual-income households report doing more cognitive labor
            than their partner, even when both work full time.{' '}
            <AppendixStatSource>(Weeks &amp; Ruppanner, 2025)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>&uarr;2&times;</AppendixStatNumber>
          <AppendixStatDescription>
            Fathers consistently overestimate their contribution to cognitive household
            labor. The perception gap itself prevents change.{' '}
            <AppendixStatSource>(Daminger, 2019)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AccentCalloutBox
          $accent={colors.accent.purple.exxtraDark}
          $bg={colors.accent.purple.extraLight}
        >
          <AccentCalloutTitle $color={colors.accent.purple.exxtraDark}>
            &ldquo;Gendered Cognitive Stickiness&rdquo;
          </AccentCalloutTitle>
          <AccentCalloutBody>
            Once a mother begins handling a cognitive domain (medical appointments,
            school logistics, meal planning), it becomes &ldquo;hers&rdquo; permanently.
            The knowledge accumulates, making handoff increasingly difficult.
            Even when fathers take on physical tasks, the <em>thinking</em> stays with mom.{' '}
            <AppendixStatSource>(Weeks &amp; Ruppanner, 2025)</AppendixStatSource>
          </AccentCalloutBody>
        </AccentCalloutBox>

        <AccentCalloutBox
          $accent={colors.accent.yellow.exxtraDark}
          $bg={colors.accent.yellow.extraLight}
        >
          <ItalicCalloutBody>
            &ldquo;What do you need me to do?&rdquo; &mdash; the question itself is the problem.
            It positions one partner as the <QuoteAttribution>manager</QuoteAttribution> and the
            other as a <QuoteAttribution>helper</QuoteAttribution>. The helper gets a task; the
            manager keeps the full cognitive load of knowing what needs doing, when, for whom,
            and how. &mdash; <QuoteAttribution>Eve Rodsky, Fair Play</QuoteAttribution>
          </ItalicCalloutBody>
        </AccentCalloutBox>
      </AppendixColumn>

      <AppendixColumn>
        <AppendixSectionLabel>3 Structural Barriers to Delegation</AppendixSectionLabel>

        <AccentBorderedCard $accent={colors.accent.coral.exxtraDark}>
          <AccentBorderedCardTitle>1. Tacit Knowledge &mdash; It Lives in Mom&rsquo;s Head</AccentBorderedCardTitle>
          <AccentBorderedCardBody>
            Mom knows the pediatrician&rsquo;s preferred booking window, which kid won&rsquo;t eat
            bell peppers this month, the neighbor who can do emergency pickup, and the teacher
            who needs email not text. This knowledge is undocumented, interconnected, and built
            over years. There&rsquo;s no system to externalize it.{' '}
            <AppendixStatSource>(Wegner, 1987 &mdash; Transactive Memory)</AppendixStatSource>
          </AccentBorderedCardBody>
        </AccentBorderedCard>

        <AccentBorderedCard $accent={colors.accent.blue.exxtraDark}>
          <AccentBorderedCardTitle>2. Siloed Data &mdash; It Lives in Mom&rsquo;s Apps</AccentBorderedCardTitle>
          <AccentBorderedCardBody>
            Medical records are in MyChart under her login. School updates come to her email.
            The grocery list is on her phone. The budget spreadsheet is in her Google Drive.
            Even a willing partner can&rsquo;t access the information needed to take ownership
            of a domain without going through mom first.
          </AccentBorderedCardBody>
        </AccentBorderedCard>

        <AccentBorderedCard $accent={colors.accent.purple.exxtraDark}>
          <AccentBorderedCardTitle>3. Relational Capital &mdash; It&rsquo;s Mom&rsquo;s Network</AccentBorderedCardTitle>
          <AccentBorderedCardBody>
            The pediatrician, the tutor, the soccer coach, the carpool group, the PTA
            chair &mdash; these relationships are built with mom. Delegation requires
            transferring not just tasks but <em>trust and context</em> across an entire
            network of external stakeholders.{' '}
            <AppendixStatSource>(BPS, 2025)</AppendixStatSource>
          </AccentBorderedCardBody>
        </AccentBorderedCard>

        <DarkInsightBox>
          <DarkInsightTitle>Key Insight</DarkInsightTitle>
          <DarkInsightBody>
            Women&rsquo;s employment reduces the gap in <DarkInsightEmphasis>physical</DarkInsightEmphasis> household
            labor but has <DarkInsightEmphasis>almost no effect on cognitive labor</DarkInsightEmphasis>.
            Working mothers carry the same thinking burden as stay-at-home mothers &mdash;
            they just have less time for it. The bottleneck isn&rsquo;t willingness.
            It&rsquo;s infrastructure.{' '}
            <AppendixStatSource>(Weeks &amp; Ruppanner, 2025)</AppendixStatSource>
          </DarkInsightBody>
        </DarkInsightBox>
      </AppendixColumn>
    </AppendixContentArea>

    <AppendixSourceFooter>
      Weeks &amp; Ruppanner (2024, 2025) &middot; Daminger (2019) &middot;
      Eve Rodsky, <em>Fair Play</em> (2019) &middot; Wegner, Transactive Memory (1987) &middot;
      BPS Household Labor Report (2025)
    </AppendixSourceFooter>
  </AppendixSlideLayout>
);
